<template>
  <div class="info-check-view container">
    <div class="breadcrumb">
      <Breadcrumb />
    </div>
    <div>
      <Wizzard current-page="check" />
    </div>
    <div class="title-info-check text-start">
      <h1 class="mb-5">Confira seus dados.</h1>
    </div>
    <div class="alert alert-dismissible alert-warning" role="alert">
      <div class="alert__icon">
        <Icon name="circle-exclamation" />
      </div>
      <div class="alert__content text-start">
        Atenção! Verifique se os dados estão corretos antes de finalizar, pois
        não será possível fazer alterações após a emissão do Certificado
        Digital.
      </div>
    </div>
    <DynamicSummary />
    <div class="row d-flex">
      <div class="col-12 mt-3 justify-content-end d-flex flex-row">
        <button class="col-12 col-md-3 btn btn-primary" @click="finishJorney">
          <span class="me-3">Avançar</span>
          <Icon name="arrow-right" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  // import { AxiosError } from 'axios';
  import Breadcrumb from '@/components/Molecules/Breadcrumbs.vue'
  import Wizzard from '@/components/Molecules/Wizzard.vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import { useMainStore } from '@/store/Pinia/useMain.store'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import {
    FormDataType,
    useDynamicForms,
  } from '@/store/Pinia/useDynamicForms.store'
  import { mapActions, mapState } from 'pinia'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import {
    finishJourneyAndEmit,
    FinishJourneyAndEmitType,
  } from '@/utils/finishJourney'
  import getMunicipioByNameAndUf from '@/utils/getMunicipioByNameAndUF'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import DynamicSummary from '@/components/Molecules/DynamicSummary.vue'
  import emissionController from '@/infra/BirdId/controllers/emission.controller'
  import { useAR } from '@/store/Pinia/useAR.store'
  import { ESolicitationStatus } from '@/types/enums/ESisar.enum'
  import { useOeRecaptcha } from '@/composables/useOeRecaptcha'

  export default defineComponent({
    name: 'SummaryInfo',
    components: {
      Wizzard,
      Breadcrumb,
      Icon,
      DynamicSummary,
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      error: '',
    }),
    computed: {
      ...mapState(useModalLoading, ['modalLoading']),
      ...mapState(useMainStore, ['idunidade']),
      ...mapState(useDynamicForms, ['formData', 'getPassword']),
      ...mapState(useSolicitation, ['getSolicitation', 'getSolicitationToken']),
      ...mapState(useDynamicForms, ['getFormData']),
      ...mapState(useAR, ['arData']),
      hasCei(): boolean {
        return this.getFormData.cei !== ''
      },
      hasPis(): boolean {
        return this.getFormData.pis !== ''
      },
    },
    methods: {
      ...mapActions(useModalLoading, ['setIsLoading']),
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      ...mapActions(useMainStore, ['clearBirdSessionToken']),
      async finishJorney() {
        this.setIsLoading({
          isLoading: true,
          title: 'Aguarde um pouquinho! Estamos enviando sua Solicitação',
          image: 'clock',
        })

        try {
          const municipio = getMunicipioByNameAndUf(
            this.formData.municipio,
            this.formData.uf,
          )

          if (!municipio) {
            throw new Error('Não foi possível encontrar o município')
          }

          const telefoneFormatted = this.formData.telefone
            .replace('55', '')
            .replace(/[^0-9]/g, '')

          const formattedFormData = { ...this.getFormData }

          Object.entries(this.getFormData).forEach(([key, value]) => {
            if (value === '')
              delete formattedFormData[key as keyof FormDataType]
          })

          const finishJourneyAndEmitParams = {
            sessionData: this.getSolicitationToken.sessionData,
            sessionId: this.getSolicitationToken.sessionId,
            formData: {
              ...formattedFormData,
              senha: this.getPassword,
              senhaConfirm: this.getPassword,
              municipio: municipio.idmunicipio,
              telefone: telefoneFormatted,
              email: this.formData.email.toLowerCase(),
              // ESSE CAMPO está no formattedFormData
              // business_category: this.formData.business_category,
              uf: this.formData.uf,
              cnpj: this.formData.cnpj?.replace(/[^\w]/g, ''),
              razaoSocial: this.formData.razao_social,
              idunidade: this.idunidade,
              isRFB: this.getSolicitation.isRFB,
            },
          } as FinishJourneyAndEmitType

          const response = await finishJourneyAndEmit(
            finishJourneyAndEmitParams,
          )

          // Limpa o token pois a jornada foi concluída com sucesso
          this.clearBirdSessionToken()

          const recaptchaTokenGetSolicitationData =
            await this.oeRecaptcha.getToken('getsolicitationdata')

          const solicitationData = await emissionController.getSolicitationData(
            {
              solicitacao: response.codigioSolicitacao,
              arsubdomain: this.arData.subdomain || '',
              recaptcha_token: recaptchaTokenGetSolicitationData,
            },
          )

          if (
            Number(solicitationData.situacao.id) ===
            ESolicitationStatus.APROVADA
          ) {
            this.$router.push({ name: 'ReservedDocument' })
            return
          }

          if (response.isQsa && this.getSolicitation.isSSL) {
            this.$router.push({ path: '/domain-validation' })
            return
          }

          if (!this.getSolicitation.isPj && (this.hasCei || this.hasPis)) {
            this.$router.push({ path: '/upload-documents-pis-cei' })
            return
          }

          if (!this.getSolicitation.isPj || response.isQsa) {
            const route = this.getSolicitation.isBirdId
              ? '/installation-guide-birdid'
              : '/installation-guide-token'
            this.$router.push({ path: route })
            return
          }

          this.$router.push({ path: '/upload-documents' })

          return
        } catch (error) {
          const err = error as Error | any
          this.error = err.message
          let errorMessage =
            'Houve um erro ao tentar atualizar os dados da solicitação'
          if (err.response) {
            if (
              err.response.data.message &&
              err.response?.data.message !== ''
            ) {
              errorMessage = err.response?.data.message
            }
          }

          this.toggleToastGlobal({
            type: 'danger',
            isShow: true,
            message: errorMessage,
          })
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 100)
        }
      },
    },
    beforeRouteEnter(to, from, next) {
      const solicitationStore = useSolicitation()
      if (
        // eslint-disable-next-line operator-linebreak
        solicitationStore.getSolicitationToken.sessionData === '' ||
        // eslint-disable-next-line operator-linebreak
        solicitationStore.getSolicitationToken.sessionId === '' ||
        solicitationStore.$state.solicitation.voucher === ''
      ) {
        return next('/')
      }
      return next()
    },
  })
</script>

<style scoped></style>
